import { normalize } from 'normalizr'
import { Schemas } from 'redux/schema'
import { updateEntities, deleteEntity, overwriteEntity } from 'redux/entities'

import apiV2 from 'utils/apiV2'

import { handleError } from '../utils'

const FETCH_REQUEST = 'contenthouse/project/FETCH_REQUEST'
const FETCH_SUCCESS = 'contenthouse/project/FETCH_SUCCESS'
const FETCH_FAILURE = 'contenthouse/project/FETCH_FAILURE'

const CREATE_REQUEST = 'contenthouse/project/CREATE_REQUEST'
const CREATE_SUCCESS = 'contenthouse/project/CREATE_SUCCESS'
const CREATE_FAILURE = 'contenthouse/project/CREATE_FAILURE'

const UPDATE_REQUEST = 'contenthouse/project/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'contenthouse/project/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'contenthouse/project/UPDATE_FAILURE'

const DELETE_REQUEST = 'contenthouse/project/DELETE_REQUEST'
const DELETE_SUCCESS = 'contenthouse/project/DELETE_SUCCESS'
const DELETE_FAILURE = 'contenthouse/project/DELETE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loading: false,
  result: {},
  updating: false,
}

// Actions
export function fetchRequest() {
  return {
    type: FETCH_REQUEST,
  }
}

export function fetchSuccess(payload = {}) {
  return {
    type: FETCH_SUCCESS,
    payload,
  }
}

export function fetchFailure(errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadProjects(options) {
  const config = {
    headers: {
      ...(options?.continuationToken && !options?.overwrite && { 'x-continuation-token': options.continuationToken }),
    },
  }

  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = apiV2('/projects', options.payload, config)
      .then(({ data, headers }) => {
        const normalizedJson = normalize(data, Schemas.PROJECT_ARRAY)

        if (options?.overwrite) {
          if (!normalizedJson?.entities?.projects) normalizedJson.entities = { projects: {} }
          dispatch(overwriteEntity(normalizedJson))
        } else {
          dispatch(updateEntities(normalizedJson))
        }

        const updateData = {
          projects: data || [],
          continuationToken: headers['x-continuation-token'],
        }
        dispatch(fetchSuccess(updateData))

        return { success: true, data: updateData }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function loadProject(id, options) {
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = apiV2(`/projects/${id}`, options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PROJECT)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(data))

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function createProject(project, options = {}) {
  const config = {
    method: 'POST',
    data: project,
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = apiV2(`/projects`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PROJECT)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, result: data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function createProjectPublicLink(project, options = {}) {
  const config = {
    method: 'POST',
    data: project.data,
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = apiV2(`/projects/${project.id}/public-links`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PROJECT)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, result: data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function updateProject(project, options = {}) {
  const config = {
    method: 'PUT',
    data: project,
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = apiV2(`/projects/${project.id}`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PROJECT)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, result: data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function patchProject(project, options = {}) {
  const { id, ...payload } = project
  const config = {
    method: 'PATCH',
    data: payload,
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = apiV2(`/projects/${id}`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PROJECT)
        const updateEntityOptions = {
          mergeOptions: {
            customMerge: () => (_, source) => source,
          },
        }

        dispatch(updateEntities(normalizedJson, updateEntityOptions))
        dispatch(updateSuccess())

        return { success: true, result: data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function deleteProjectPublicLink(project, options = {}) {
  const config = {
    method: 'DELETE',
    data: project.data,
  }

  return (dispatch) => {
    dispatch(deleteRequest())

    const promise = apiV2(`/projects/${project.id}/public-links`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PROJECT)
        dispatch(updateEntities(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true, result: data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}) {
  const { type, payload, errors } = action

  switch (type) {
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
        result: payload,
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors,
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors,
      }
    default:
      return state
  }
}
